import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { BrowserRouter } from 'react-router-dom';
import Hotjar from '@hotjar/browser';
// Accepting transitive dep as safe, given that you'd assume use-immer will always, necessarily,
// depend on immer
// eslint-disable-next-line import/no-extraneous-dependencies
import { enableMapSet } from 'immer';

// REDUX
import { createStore, applyMiddleware, compose } from 'redux';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';

import { initSentry } from './utils/sentry';

import ErrorFallback from './components/error-fallback';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';

import reducers from './reducers';

import App from './routes';

import Initializers from './initializers';

import 'sanitize.css/sanitize.css';
import './index.css';
import ScrollToTop from './components/scroll-to-top';
import { GoogleAnalyticsProvider } from './components/google-analytics';

// Required polyfills
// Object.hasOwn used in SDM / learn flows
import 'core-js/actual/object/has-own';

enableMapSet();

initSentry();

const initialState = {};
const enhancers = [];
const middleware = [thunk];

// hotjar init
const siteId = 3913880;
const hotjarVersion = 6;
Hotjar.init(siteId, hotjarVersion);

if (process.env.NODE_ENV === 'development') {
    const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__;

    if (typeof devToolsExtension === 'function') {
        enhancers.push(devToolsExtension());
    }
}

if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line global-require
    const axe = require('@axe-core/react');
    axe(React, ReactDOM, 1000);
}

const composedEnhancers = compose(applyMiddleware(...middleware), ...enhancers);

const store = createStore(
    reducers(),
    initialState,
    composedEnhancers,
);

const onBeforeLift = () => {
    // Run initializers... anything that will need to use or subscribe to the store
    Initializers(store);
    window.Weglot.initialize({
        api_key: process.env.REACT_APP_WEGLOT,
        hide_switcher: true,
    });
};

onBeforeLift();

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
    <Provider store={store}>
        <BrowserRouter>
            <GoogleAnalyticsProvider>
                <Sentry.ErrorBoundary fallback={() => <ErrorFallback />}>
                    <ScrollToTop />
                    <App store={store} />
                </Sentry.ErrorBoundary>
            </GoogleAnalyticsProvider>
        </BrowserRouter>
    </Provider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
